import { FormikField } from "@components/forms/FormikField/FormikField";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import {
    TagsInput,
    type TagsInputSize,
} from "@design-system/TagsInput/TagsInput";
import type { FieldPath } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import type { KitFieldProps } from "./kitFieldProps.types";
import { useEmailTagsRules } from "./useEmailTagsRules";

type EmailsFieldValue = string[] | null | undefined;

/** Paths of `Values` that hold a list of email addresses. */
export type EmailsFieldPath<Values> = FieldPath<
    Values,
    EmailsFieldValue,
    string[]
>;

export type EmailsFieldProps<Name extends string> = KitFieldProps<
    Name,
    HTMLInputElement,
    string[]
> & {
    placeholder?: string;
    size?: TagsInputSize;
};

/**
 * Email list field bound to a typed Formik path. Entries are lowercased,
 * invalid addresses are rejected on entry with a toast, and leaving the
 * field marks it touched.
 */
export const EmailsField = <
    Values,
    Name extends EmailsFieldPath<Values> = EmailsFieldPath<Values>,
>(
    props: EmailsFieldProps<Name>,
) => {
    const { name, ref, disabled, required, onValueChange, placeholder, size } =
        props;
    const emailRules = useEmailTagsRules();

    return (
        <FormikField<EmailsFieldValue> name={name} {...pickChromeProps(props)}>
            {({ field, helpers }) => (
                <TagsInput
                    {...emailRules}
                    ref={ref}
                    name={name}
                    value={field.value ?? []}
                    onValueChange={(emails) => {
                        void helpers.setValue(emails);
                        onValueChange?.(emails);
                    }}
                    onBlur={() => void helpers.setTouched(true)}
                    aria-required={required || undefined}
                    {...ariaLabelledbyProps(props["aria-labelledby"])}
                    disabled={disabled}
                    placeholder={placeholder}
                    size={size}
                />
            )}
        </FormikField>
    );
};
